/**
 * REJESTR DANYCH EDUKACYJNYCH (wszystkie biomy edukacyjne).
 *
 * Scala datasety per-temat w jeden `EDU_ENTRIES` kluczowany po `petId`.
 * Dodanie nowego biomu edukacyjnego (np. kolejny kontynent, planety, dinozaury):
 *   1. utwórz `src/config/edu/<temat>.ts` eksportujący `Record<petId, EduEntry>`,
 *   2. zaimportuj i rozłóż go tutaj w `EDU_ENTRIES`,
 *   3. dopisz wpis w `EDU_BIOME_UI` (napisy paska nauki — patrz niżej).
 * Reszta (karta „Nauka", `EduInteractBar`, dymki) działa bez zmian.
 */

import type { EduEntry } from '@/domain/types/edu';
import type { ID } from '@/domain/types/common';
import { FLAGS_EUROPE_EDU } from './flags-europe';
import { PUSZCZA_EDU } from './puszcza-niepolomicka';

export const EDU_ENTRIES: Record<ID, EduEntry> = {
  ...FLAGS_EUROPE_EDU,
  ...PUSZCZA_EDU,
};

/** Zwraca dane edukacyjne stworka albo `undefined` (pet bez wartości edukacyjnej). */
export function getEdu(petId: ID): EduEntry | undefined {
  return EDU_ENTRIES[petId];
}

// ─────────────────────────────────────────────────────────────────────────────
// NAPISY PASKA NAUKI (EduInteractBar) — per biom edukacyjny
// ─────────────────────────────────────────────────────────────────────────────
//
// Do wersji z jednym biomem edukacyjnym `EduInteractBar` miał napisy wpisane na
// sztywno („Flagi: nauka", „Wybierz flagę", „Zabytek"). Przy drugim biomie
// (Puszcza) byłoby to po prostu nieprawdą na ekranie, więc teksty wyjechały do
// configu. Mechanika paska się NIE zmienia — to wyłącznie warstwa słów.
//
// Nowy biom edukacyjny bez wpisu tutaj dostanie neutralny `EDU_BIOME_UI_DEFAULT`
// (działa, tylko mówi ogólnie „stworek"), więc brak wpisu nie psuje gry.

export interface EduBiomeUi {
  /** Napis na zwiniętym przycisku paska, np. „Puszcza: nauka". */
  barLabel: string;
  /** Nagłówek gridu wyboru, np. „Wybierz mieszkańca". */
  pickerTitle: string;
  /** aria-label strzałki powrotu do gridu. */
  backAriaLabel: string;
  /** Podpowiedź, gdy gracz nie ma ŻADNEGO peta z tego biomu. */
  emptyOwnedHint: string;
  /** Podpowiedź, gdy pety są, ale żaden nie jest założony (equipped). */
  emptyEquippedHint: string;
  /** Etykieta przycisku otwierającego kartę `landmark`. */
  landmarkLabel: string;
}

export const EDU_BIOME_UI_DEFAULT: EduBiomeUi = {
  barLabel: 'Nauka',
  pickerTitle: 'Wybierz stworka',
  backAriaLabel: 'Wróć do wyboru stworka',
  emptyOwnedHint: 'Wyklujcie stworki z jajka tego świata, aby poznać ich sekrety.',
  emptyEquippedHint: 'Załóżcie stworka z tego biomu (przycisk „Załóż" w pecie), żeby z nim porozmawiać.',
  landmarkLabel: 'Zobacz',
};

export const EDU_BIOME_UI: Record<ID, EduBiomeUi> = {
  'biome-flags-europe': {
    barLabel: 'Flagi: nauka',
    pickerTitle: 'Wybierz flagę',
    backAriaLabel: 'Wróć do wyboru flagi',
    emptyOwnedHint: 'Wyklujcie flagi z jajka „Flagi Europy", aby poznać ich powitania i ciekawostki.',
    emptyEquippedHint: 'Załóżcie flagę z tego biomu (przycisk „Załóż" w pecie), żeby z nią porozmawiać.',
    landmarkLabel: 'Zabytek',
  },
  'biome-puszcza': {
    barLabel: 'Puszcza: nauka',
    pickerTitle: 'Wybierz mieszkańca puszczy',
    backAriaLabel: 'Wróć do wyboru mieszkańca puszczy',
    emptyOwnedHint: 'Wyklujcie stworki z „Jajka Runa Leśnego", aby poznać grzyby, drzewa i zwierzęta puszczy.',
    emptyEquippedHint: 'Załóżcie mieszkańca puszczy (przycisk „Załóż" w pecie), żeby go posłuchać.',
    // Karta pokazuje realistyczne zdjęcie gatunku — sprite peta to urocza
    // naklejka, a dziecko musi wiedzieć, jak ten grzyb czy zwierzę wygląda
    // NAPRAWDĘ (przy muchomorze sromotnikowym to kwestia bezpieczeństwa).
    landmarkLabel: 'Naprawdę',
  },
};

/** Napisy paska nauki dla biomu; fallback dla biomu bez własnego wpisu. */
export function getEduBiomeUi(biomeId: ID): EduBiomeUi {
  return EDU_BIOME_UI[biomeId] ?? EDU_BIOME_UI_DEFAULT;
}
